import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, ArrowLeft, Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '../lib/auth';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_securedeck/artifacts/7gltxyqf_BSMS%20Rapid%20X.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devToken, setDevToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setDevToken(null);
    setLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { email });
      setSuccess(true);
      
      // DEV ONLY: Show the token (remove in production)
      if (response.data.dev_reset_token) {
        setDevToken(response.data);
      }
    } catch (err) {
      // Should not happen since API always returns 200
      setError(err.response?.data?.detail || err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-8">
      <Card className="w-full max-w-md bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="space-y-1 text-center pb-6">
          <div className="flex justify-center mb-4">
            <img 
              src={LOGO_URL} 
              alt="BSMS Rapid X" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            FORGOT PASSWORD
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Enter your email to receive a password reset link
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="p-4 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-emerald-400 font-medium">Check your email</p>
                  <p className="text-xs text-zinc-400 mt-1">
                    If an account exists with this email, a password reset link has been sent.
                  </p>
                </div>
              </div>
              
              {/* DEV ONLY: Show reset link */}
              {devToken && (
                <div className="p-4 rounded-sm bg-amber-500/10 border border-amber-500/30">
                  <p className="text-xs text-amber-400 font-medium mb-2">DEV MODE: Reset Link</p>
                  <Link 
                    to={devToken.dev_reset_link}
                    className="text-sm text-blue-400 hover:text-blue-300 underline break-all"
                  >
                    {window.location.origin}{devToken.dev_reset_link}
                  </Link>
                  <p className="text-xs text-zinc-500 mt-2">
                    Token expires in 30 minutes
                  </p>
                </div>
              )}
              
              <Link to="/login">
                <Button
                  variant="outline"
                  className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/30 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300 font-inter">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@bsms.net.au"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-[#09090b] border-[#3f3f46] text-white placeholder:text-zinc-600 font-mono text-sm focus:ring-1 focus:ring-blue-500"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-rajdhani font-semibold tracking-wide rounded-sm"
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    SENDING...
                  </>
                ) : (
                  'SEND RESET LINK'
                )}
              </Button>
              
              <div className="pt-4 border-t border-[#3f3f46]">
                <Link to="/login">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-zinc-400 hover:text-zinc-200"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
